import { useState } from "react";
import { useAuth } from "../../auth/useAuth";
import { RecoveryCodeModal } from "./RecoveryCodeModal";
import * as S from "./sheet";

export function SignupScreen({ onClose, onSwitchToLogin }: { onClose: () => void; onSwitchToLogin: () => void }) {
  const { signup } = useAuth();
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signup({
        email,
        username,
        password,
        displayName: displayName.trim() || undefined,
      });
      setRecoveryCode(result.recoveryCode);
    } catch (err) {
      setError((err as { message?: string }).message ?? "Sign-up failed.");
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCode) {
    return (
      <RecoveryCodeModal
        code={recoveryCode}
        context="signup"
        onConfirm={() => {
          setRecoveryCode(null);
          onClose();
        }}
      />
    );
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.card} onClick={(e) => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.title}>Create account</span>
          <button onClick={onClose} style={S.closeButton} aria-label="Close">
            ×
          </button>
        </div>
        <form onSubmit={submit} style={S.body}>
          <div style={S.field}>
            <label style={S.label}>Email</label>
            <input
              style={S.input}
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div style={S.field}>
            <label style={S.label}>Username</label>
            <input
              style={S.input}
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              minLength={3}
              maxLength={20}
              pattern="[A-Za-z0-9_-]{3,20}"
              required
            />
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              3–20 characters: letters, numbers, hyphens, underscores.
            </span>
          </div>

          <div style={S.field}>
            <label style={S.label}>Display name (optional)</label>
            <input
              style={S.input}
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={60}
              placeholder="What other players will see"
            />
          </div>

          <div style={S.field}>
            <label style={S.label}>Password</label>
            <input
              style={S.input}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={10}
              required
            />
            <span style={{ fontSize: 12, opacity: 0.7 }}>At least 10 characters. Use whatever your password manager makes you.</span>
          </div>

          {error && <div style={S.errorText}>{error}</div>}

          <button type="submit" style={{ ...S.primaryButton, opacity: busy ? 0.6 : 1 }} disabled={busy}>
            {busy ? "Creating account…" : "Create account"}
          </button>

          <div style={{ marginTop: 4 }}>
            <button type="button" style={S.linkButton} onClick={onSwitchToLogin}>
              I already have an account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
